import { TextChunk } from "./chunker";

/**
 * Schedules TTS audio for a streamed answer's chunks strictly in order on a
 * caller-supplied AudioContext (must be created on a user gesture — that's
 * the caller's job, not this module's). Chunks are pushed as the chunker
 * produces them; this engine fetches each one's audio immediately (so
 * chunk N+1 is already fetching while chunk N plays) but only ever
 * *schedules* them in order — a fetch finishing out of order never causes a
 * reorder. One chunk failing to decode/fetch is skipped, not fatal.
 *
 * Highlighting is driven by reading `AudioContext.currentTime` on a
 * requestAnimationFrame loop, never setInterval (which drifts relative to
 * the audio clock).
 */

export interface AudioFetchResult {
  bytes: ArrayBuffer;
  mimeType: string;
}

export type AudioFetchFn = (chunk: TextChunk, signal: AbortSignal) => Promise<AudioFetchResult>;

export interface HighlightRange {
  /** Offsets into the full message text (same coordinate space as TextChunk). */
  chunkStart: number;
  chunkEnd: number;
  /** How far into the chunk to highlight, as an absolute offset (chunkStart
   * <= highlightEnd <= chunkEnd), advancing across the chunk's words
   * allocated by character length as elapsed/duration progresses. */
  highlightEnd: number;
}

export interface PlaybackCallbacks {
  onHighlight: (range: HighlightRange | null) => void;
  onDone: () => void;
  onChunkFailed?: (chunk: TextChunk, error: unknown) => void;
}

interface ScheduledEntry {
  chunk: TextChunk;
  startTime: number;
  duration: number;
}

export class PlaybackEngine {
  private audioContext: AudioContext;
  private fetchFn: AudioFetchFn;
  private callbacks: PlaybackCallbacks;

  private queue: TextChunk[] = [];
  private audioPromises: Map<number, Promise<AudioFetchResult | null>> = new Map();
  private nextScheduleIndex = 0;
  private scheduling = false;
  private scheduleCursorTime = 0;

  private sources: AudioBufferSourceNode[] = [];
  private endedCount = 0;
  private activeSchedule: ScheduledEntry[] = [];

  private streamEnded = false;
  private stopped = false;
  private doneCalled = false;

  private abortController = new AbortController();
  private highlightRafId: number | null = null;
  private lastReportedHighlightEnd: number | null = null;
  private lastReportedChunkStart: number | null = null;

  constructor(audioContext: AudioContext, fetchFn: AudioFetchFn, callbacks: PlaybackCallbacks) {
    this.audioContext = audioContext;
    this.fetchFn = fetchFn;
    this.callbacks = callbacks;
    this.scheduleCursorTime = audioContext.currentTime;
  }

  /** Feed the next complete chunk. Fetches its audio immediately. */
  push(chunk: TextChunk): void {
    if (this.stopped) return;
    const index = this.queue.length;
    this.queue.push(chunk);
    const promise = this.fetchFn(chunk, this.abortController.signal).catch((err) => {
      this.callbacks.onChunkFailed?.(chunk, err);
      return null;
    });
    this.audioPromises.set(index, promise);
    void this.tryScheduleNext();
  }

  /** Signal that no more chunks are coming (the answer finished streaming). */
  end(): void {
    this.streamEnded = true;
    this.maybeFinish();
  }

  /** Stop everything immediately: sources, in-flight fetches, highlight. */
  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    this.abortController.abort();
    for (const s of this.sources) {
      try {
        s.onended = null;
        s.stop();
      } catch {
        // already stopped/ended — fine
      }
    }
    this.sources = [];
    this.activeSchedule = [];
    if (this.highlightRafId !== null) {
      cancelAnimationFrame(this.highlightRafId);
      this.highlightRafId = null;
    }
    this.callbacks.onHighlight(null);
  }

  private async tryScheduleNext(): Promise<void> {
    if (this.stopped || this.scheduling) return;
    this.scheduling = true;
    try {
      while (this.nextScheduleIndex < this.queue.length) {
        const idx = this.nextScheduleIndex;
        const promise = this.audioPromises.get(idx);
        if (!promise) break; // this chunk hasn't been push()-ed with a fetch yet (shouldn't happen)

        const result = await promise;
        if (this.stopped) return;

        if (result === null) {
          this.nextScheduleIndex++;
          continue; // fetch/decode already reported via onChunkFailed
        }

        let audioBuffer: AudioBuffer | null = null;
        try {
          audioBuffer = await this.audioContext.decodeAudioData(result.bytes.slice(0));
        } catch (err) {
          this.callbacks.onChunkFailed?.(this.queue[idx], err);
        }
        if (this.stopped) return;

        if (!audioBuffer) {
          this.nextScheduleIndex++;
          continue;
        }

        const startTime = Math.max(this.scheduleCursorTime, this.audioContext.currentTime);
        const source = this.audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(this.audioContext.destination);
        source.onended = () => {
          this.endedCount++;
          this.maybeFinish();
        };
        source.start(startTime);

        this.sources.push(source);
        this.activeSchedule.push({ chunk: this.queue[idx], startTime, duration: audioBuffer.duration });
        this.scheduleCursorTime = startTime + audioBuffer.duration;
        this.nextScheduleIndex++;

        if (this.highlightRafId === null) this.startHighlightLoop();
      }
    } finally {
      this.scheduling = false;
      this.maybeFinish();
    }
  }

  private maybeFinish(): void {
    if (this.doneCalled || this.stopped) return;
    if (this.streamEnded && this.nextScheduleIndex >= this.queue.length && this.endedCount >= this.sources.length) {
      this.doneCalled = true;
      if (this.highlightRafId !== null) {
        cancelAnimationFrame(this.highlightRafId);
        this.highlightRafId = null;
      }
      this.callbacks.onHighlight(null);
      this.callbacks.onDone();
    }
  }

  private startHighlightLoop(): void {
    const loop = () => {
      if (this.stopped) {
        this.highlightRafId = null;
        return;
      }
      const now = this.audioContext.currentTime;
      const active = this.activeSchedule.find((s) => now >= s.startTime && now < s.startTime + s.duration);
      if (active) {
        const elapsed = now - active.startTime;
        const ratio = Math.min(Math.max(elapsed / active.duration, 0), 1);
        const highlightEnd = active.chunk.start + wordAlignedOffset(active.chunk.text, ratio);
        // Only push a React update when the word-level offset actually
        // changes — sub-word movement within one rAF tick is invisible and
        // would otherwise re-render the message component ~60x/sec.
        if (highlightEnd !== this.lastReportedHighlightEnd || active.chunk.start !== this.lastReportedChunkStart) {
          this.lastReportedHighlightEnd = highlightEnd;
          this.lastReportedChunkStart = active.chunk.start;
          this.callbacks.onHighlight({
            chunkStart: active.chunk.start,
            chunkEnd: active.chunk.end,
            highlightEnd,
          });
        }
      }
      this.highlightRafId = requestAnimationFrame(loop);
    };
    this.highlightRafId = requestAnimationFrame(loop);
  }
}

// Maps an elapsed/duration ratio to a character offset within `text`,
// snapped forward to the end of the word it falls inside — this is what
// "allocate elapsed/duration across words by character length" reduces to:
// a uniform per-character time model naturally gives each word a time-share
// proportional to its length, without a separate weighting pass.
function wordAlignedOffset(text: string, ratio: number): number {
  const totalLen = text.length || 1;
  const target = Math.min(Math.floor(ratio * totalLen), text.length);
  let i = target;
  while (i < text.length && !/\s/.test(text[i])) i++;
  return i;
}
